import { Helmet } from 'react-helmet-async'
import { GLOBAL_DEFAULT_SEO, SEO_SITE_NAME } from '@/lib/seo/defaults'
import { buildPrivateDashboardSeo } from '@/lib/seo/generators/static'
import { resolveSeo } from '@/lib/seo/resolve'
import { buildRobotsContent, normalizeSeoTitle, ogImageMimeType } from '@/lib/seo/utils'
import type { ResolvedSeo } from '@/lib/seo/types'

export type { ResolvedSeo, SeoAdminFields } from '@/lib/seo/types'

function normalizeSchemas(schema?: ResolvedSeo['schema']) {
  if (!schema) return []
  return Array.isArray(schema) ? schema : [schema]
}

/** @deprecated Prefer PageSeo with ResolvedSeo from lib/seo */
export interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  canonical?: string
  ogImage?: string
  ogType?: string
  noIndex?: boolean
  noFollow?: boolean
  schema?: Record<string, unknown>
}

export function PageSeo({ seo }: { seo: ResolvedSeo }) {
  const rawTitle = seo.title.includes('Quick Build Con') || seo.title.includes('QBC')
    ? seo.title
    : `${seo.title} | Quick Build Con`
  const title = normalizeSeoTitle(rawTitle)
  const robots = buildRobotsContent(seo.noIndex, seo.noFollow)
  const schemas = normalizeSchemas(seo.schema)
  const ogType = seo.ogType || 'website'
  const siteName = seo.siteName || SEO_SITE_NAME
  const twitterCard = seo.twitterCard || 'summary_large_image'
  const ogTypeMime = seo.ogImage ? ogImageMimeType(seo.ogImage) : null

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={seo.description} />
      {seo.keywords ? <meta name="keywords" content={seo.keywords} /> : null}
      <meta name="robots" content={robots} />
      {seo.canonical && <link rel="canonical" href={seo.canonical} />}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={seo.ogLocale || 'en_IN'} />
      {seo.canonical && <meta property="og:url" content={seo.canonical} />}
      {seo.ogImage && <meta property="og:image" content={seo.ogImage} />}
      {seo.ogImage && ogTypeMime && <meta property="og:image:type" content={ogTypeMime} />}
      {seo.ogImage && <meta property="og:image:alt" content={title} />}

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={seo.description} />
      {seo.ogImage && <meta name="twitter:image" content={seo.ogImage} />}

      {schemas.map((item, index) => (
        <script key={`schema-${index}`} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  )
}

/** Account, checkout, and other pages that must not be indexed. */
export function PrivatePageSeo({ title, description }: { title: string; description?: string }) {
  return (
    <PageSeo
      seo={resolveSeo({
        ...buildPrivateDashboardSeo(title),
        ...(description ? { description } : {}),
      })}
    />
  )
}

/** Legacy wrapper — maps simple props to centralized SEO resolver. */
export function SEOHead({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  noIndex,
  noFollow,
  schema,
}: SEOProps) {
  return (
    <PageSeo
      seo={resolveSeo(
        {
          title: title ?? GLOBAL_DEFAULT_SEO.title,
          description: description ?? GLOBAL_DEFAULT_SEO.description,
          keywords,
          canonical,
          ogImage,
          noIndex,
          noFollow,
          schema,
        },
        null,
      )}
    />
  )
}
